import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import { Box, Flex } from 'rebass';
import { Label } from '../Form';
import ToggleGroupField from '../ToggleGroupField';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { UserItem, UserItemAdmin, UserRole } from '../../pages/users/Users';
import useMode from '../../hooks/useMode';
import useNotify from '../../hooks/useNotify';

import { impersonate } from '../../services/auth';
import { ApolloError, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql/language/ast';
import useSessionInfo from '../../hooks/useSessionInfo';
import Form from '../Form/Form';
import ContactProfileForm from '../Profile/ContactProfileForm';
import { ContactProfileInput } from '../Profile/types';
import UserLoginForm, { UserLoginInput } from './UserLoginForm';
import { UserStatus } from './types';
import StatusChip from '../Status/StatusChip';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import ModalHeader from '../Modal/ModalHeader';
import { gql } from '@/modes/company/gql';

const companyRoles: { type: UserRole; option: string }[] = [
  { type: 'ADMIN', option: 'Admin' },
  { type: 'USER', option: 'User' },
  { type: 'READONLY', option: 'Readonly' },
];

const waterSupplierRoles: { type: UserRole; option: string }[] = [
  { type: 'ADMIN', option: 'Admin' },
  { type: 'USER', option: 'User' },
  { type: 'READONLY', option: 'Readonly' },
];

const ADD_USER = gql(`
  mutation AddUser($input: CompanyUserInput!) {
    addUser(input: $input) {
      profileId
      profile {
        id
        type
        name
        firstName
        lastName
      }
      roles
      email
    }
  }
`);
const UPDATE_USER = gql(`
  mutation UpdateUser($userId: ID!, $input: CompanyUserInput!) {
    updateUser(userId: $userId, input: $input) {
      profileId
      profile {
        id
        type
        name
        firstName
        lastName
      }
      roles
      email
    }
  }
`);

const UPDATE_USER_STATUS = gql(`
  mutation SetUserStatus($userId: ID!, $status: UserStatus!) {
    setUserStatus(userId: $userId, status: $status) {
      profileId
      status
    }
  }
`);

type UserEditModalProps = {
  onClose: () => void;
  user?: UserItem | UserItemAdmin;
  action: 'add' | 'edit';
  refetch?: () => void;
  refetchQueries?: DocumentNode[];
  readOnly?: boolean;
};

type UserInput = {
  profile: ContactProfileInput;
  roles:
    | UserRole[]
    | { companyProfileId: string | null; role: UserRole | null }[];
  email: string;
  password?: string;
  currentPassword?: string;
};

const UserEditModal: React.FC<UserEditModalProps> = ({
  onClose,
  user,
  action,
  refetch,
  refetchQueries,
  readOnly = false,
}) => {
  const [userInput, setUserInput] = useState<UserInput>(
    user
      ? {
          profile: {
            type: user.profile?.type || '',
            name: user.profile?.name || '',
            firstName: user.profile?.firstName || '',
            lastName: user.profile?.lastName || '',
          },
          roles: user.roles ?? [],
          email: user.email ?? '',
          password: '',
        }
      : {
          profile: {
            type: 'INDIVIDUAL',
            name: '',
            firstName: '',
            lastName: '',
          },
          roles: [],
          email: '',
          password: '',
        }
  );

  const [confirmPassword, setConfirmPassword] = useState('');

  const mode = useMode();
  const { isWaterSupplier, session } = useSessionInfo();

  const availableRoles = isWaterSupplier ? waterSupplierRoles : companyRoles;

  const { showError, showSuccess, showWarning } = useNotify();

  const [addUser, { loading: addLoading }] = useMutation(ADD_USER, {
    onError: (error) => {
      showError('Error creating user', error);
      console.error('Error creating user', error);
    },
    refetchQueries,
  });

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onError: (error) => {
      showError('Error updating user', error);
      console.error('Error updating user', error);
    },
    refetchQueries,
  });

  const onProfileChange = useCallback<(profile: ContactProfileInput) => void>(
    (profile) => {
      const { type, name, firstName, lastName } = profile;

      setUserInput((prevState: any) => ({
        ...prevState,
        profile: {
          ...prevState.profile,
          ...{ type, name, firstName, lastName },
        },
      }));
    },
    [setUserInput]
  );

  const onLoginChange = useCallback(
    (userLogin: UserLoginInput) => {
      setConfirmPassword(userLogin.confirmPassword);

      setUserInput((prevState: any) => ({
        ...prevState,
        email: userLogin.email,
        password: userLogin.password,
        currentPassword: userLogin.currentPassword,
      }));
    },
    [setConfirmPassword, setUserInput]
  );

  const save = useCallback(async () => {
    if (action === 'add') {
      if (!userInput.password) {
        showError('A password must be added to save this user');
        return;
      }
    }
    if (userInput.profile.type === 'COMPANY' && !userInput.profile.name) {
      showError('A name must be added to save this user');
      return;
    }
    if (
      (userInput.profile.type === 'INDIVIDUAL' &&
        !userInput.profile.firstName) ||
      (userInput.profile.type === 'INDIVIDUAL' && !userInput.profile.lastName)
    ) {
      showError('A first and last name must be added to save this user');
      return;
    }
    if (!userInput.email) {
      showError('An email must be added to save this user');
      return;
    }
    // If no roles have been selected...
    if (!userInput.roles.length) {
      showError('At least one role must be selected to save this user');
      return;
    }
    // If Password and Confirm Password inputs do not match...
    if (userInput.password && userInput.password !== confirmPassword) {
      return;
    } else {
      if (userInput.password === '') {
        delete userInput.password;
      }

      switch (action) {
        case 'add':
          await addUser({
            variables: { input: userInput as UserItem },
            onCompleted: (data) => {
              showSuccess('User added');
              refetch?.();
              onClose();
            },
          });
          break;
        case 'edit':
          if (!user) return;
          await updateUser({
            variables: {
              userId: user.profileId,
              input: userInput as UserItem,
            },
            onCompleted: (data) => {
              showSuccess('User updated');
              refetch?.();
              onClose();
            },
          });
          break;
      }
    }
  }, [
    action,
    user?.profileId,
    userInput,
    confirmPassword,
    addUser,
    updateUser,
  ]);

  const [
    setUserStatus,
    { data, loading: mutationLoading, error: mutationError },
  ] = useMutation(UPDATE_USER_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting user status', error);
    }, []),
    refetchQueries,
  });

  const activateDeactivate = () => {
    if (!user) {
      return;
    }

    const newStatus =
      user.status === UserStatus.ACTIVE
        ? UserStatus.INACTIVE
        : UserStatus.ACTIVE;

    if (
      newStatus === UserStatus.INACTIVE &&
      !confirm('Are you sure you want to deactivate this user?')
    ) {
      return;
    }

    setUserStatus({
      variables: { userId: user.profileId, status: newStatus },
      onCompleted: () => {
        if (newStatus === UserStatus.INACTIVE) {
          showWarning('User deactivated');
        } else {
          showSuccess('User activated');
        }
        refetch?.();
        onClose();
      },
    });
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader
        title={
          readOnly
            ? (user?.profile.name ?? undefined)
            : action === 'edit'
              ? 'Edit User'
              : 'Add User'
        }
      >
        {action === 'edit' && (
          <StatusChip
            ml={2}
            status={user?.status === UserStatus.ACTIVE ? 'success' : 'error'}
          >
            {constantToLabel(user?.status)}
          </StatusChip>
        )}
      </ModalHeader>
      <ModalBody>
        <Form>
          <ContactProfileForm
            onChange={onProfileChange}
            value={userInput.profile}
            hide={[
              'type',
              'contactAddressId',
              'billingAddressId',
              'phones',
              'emails',
            ]}
            disabled={readOnly}
          />
          <UserLoginForm
            onChange={onLoginChange}
            value={{ ...userInput, confirmPassword }}
            disabled={readOnly}
            showCurrentPassword={
              !!user && user.profileId === session?.userProfile?.id
            }
          />
          {mode !== 'admin' && (
            <Flex mt={3} flexDirection="column">
              <Label>Role</Label>
              <ToggleGroupField
                disabled={readOnly}
                options={availableRoles.map((role) => ({
                  value: role.type,
                  label: role.option,
                }))}
                value={
                  typeof userInput.roles[0] === 'object'
                    ? (userInput.roles[0].role ?? '')
                    : userInput.roles[0]
                }
                onValueChange={(role) => {
                  setUserInput((prevState: any) => ({
                    ...prevState,
                    roles: [role],
                  }));
                }}
              />
            </Flex>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        {mode === 'admin' && (
          <Box flexGrow={1}>
            <Button
              variant="secondary"
              icon="logout"
              onClick={async () => {
                if (user) {
                  try {
                    await impersonate(user.profileId);
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }
              }}
            >
              Impersonate
            </Button>
          </Box>
        )}
        <Box flexGrow={1}>
          {action === 'edit' && !readOnly && (
            <>
              {user?.status === UserStatus.ACTIVE && (
                <Button
                  icon="slash-circle"
                  variant="danger"
                  onClick={activateDeactivate}
                >
                  Deactivate
                </Button>
              )}
              {user?.status === UserStatus.INACTIVE && (
                <Button variant="success" onClick={activateDeactivate}>
                  Activate
                </Button>
              )}
            </>
          )}
        </Box>
        <Flex justifyContent="flex-end" sx={{ gap: 3 }}>
          {!readOnly && (
            <Button
              icon="save"
              disabled={addLoading || updateLoading}
              onClick={save}
            >
              Save
            </Button>
          )}
          <Button variant="outline" icon="slash-circle" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

UserEditModal.propTypes = {
  onClose: PropTypes.any,
  user: PropTypes.any,
  action: PropTypes.any,
};
export default UserEditModal;
